import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import resetPopupGraphic from '../../../../../assets/images/reset-popup.svg';
const Overlay = styled.div `
  position: fixed;
  inset: 0;
  z-index: 8;
  background: rgba(5, 8, 12, 0.62);
  cursor: pointer;
`;
const Bubble = styled.div `
  position: absolute;
  left: 50%;
  bottom: 8.6rem;
  transform: translateX(-50%);
  width: min(34.5rem, 47vw);
  min-height: 12.9rem;
  border-radius: 1.35rem;
  background: linear-gradient(
    90deg,
    rgba(50, 66, 85, 0.98) 0%,
    rgba(39, 47, 58, 0.98) 20%,
    rgba(33, 40, 49, 0.98) 58%,
    rgba(35, 42, 51, 0.98) 100%
  );
  box-shadow:
    inset 0 1px 0 rgba(138, 156, 178, 0.12),
    0 28px 54px rgba(0, 0, 0, 0.34);
  display: grid;
  grid-template-columns: minmax(12rem, 15.2rem) 1fr;
  gap: 0.15rem;
  align-items: center;
  padding: 0.9rem 1.15rem 1rem 0.8rem;
  text-align: left;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -0.7rem;
    width: 1.5rem;
    height: 1.5rem;
    background: rgba(35, 42, 51, 0.98);
    transform: translateX(-50%) rotate(45deg);
    border-bottom-right-radius: 0.28rem;
  }

  @media (max-width: 1024px) {
    width: min(calc(100vw - 2rem), 31rem);
    bottom: 7.4rem;
    min-height: 11.8rem;
    padding: 0.8rem 0.9rem 0.9rem;
    grid-template-columns: minmax(10rem, 12.8rem) 1fr;
  }

  @media (max-width: 768px) {
    width: min(calc(100vw - 1.25rem), 26rem);
    bottom: 6.35rem;
    min-height: auto;
    grid-template-columns: 1fr;
    gap: 0.4rem;
    padding: 1rem 0.95rem 1.15rem;
  }
`;
const Illustration = styled.img `
  width: min(15.8rem, 20vw);
  max-width: 100%;
  height: auto;
  display: block;
  justify-self: center;
  object-fit: contain;

  @media (max-width: 1024px) {
    width: min(12rem, 22vw);
  }

  @media (max-width: 768px) {
    width: min(9.8rem, 36vw);
  }
`;
const CopyBlock = styled.div `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-left: 0.1rem;

  @media (max-width: 768px) {
    padding-left: 0;
    align-items: center;
    text-align: center;
  }
`;
const Headline = styled.div `
  display: flex;
  align-items: flex-start;
  gap: 0.68rem;
  color: #ffffff;
  line-height: 0.94;

  @media (max-width: 768px) {
    gap: 0.7rem;
    justify-content: center;
  }
`;
const Hey = styled.div `
  font-family: 'Sora', sans-serif;
  font-size: clamp(1.95rem, 3.1vw, 2.9rem);
  font-weight: 700;
  letter-spacing: -0.06em;
`;
const Question = styled.div `
  font-family: 'Sora', sans-serif;
  font-size: clamp(0.92rem, 1.32vw, 1.34rem);
  font-weight: 700;
  line-height: 1.02;
  padding-top: 0.14rem;
  white-space: pre-line;
`;
const Body = styled.div `
  margin-top: 0.72rem;
  color: rgba(200, 210, 222, 0.9);
  font-family: 'Sora', sans-serif;
  font-size: clamp(0.76rem, 0.92vw, 0.95rem);
  font-weight: 400;
  line-height: 1.4;
  max-width: 16.5rem;
`;
const CountdownLabel = styled.div `
  margin-top: 1.25rem;
  color: #ffffff;
  font-family: 'Sora', sans-serif;
  font-size: clamp(0.98rem, 1.28vw, 1.35rem);
  font-weight: 700;
  line-height: 1.02;

  @media (max-width: 768px) {
    margin-top: 1.6rem;
  }
`;
const Counter = styled.div `
  margin-top: 0.05rem;
  color: #ffffff;
  font-family: 'Sora', sans-serif;
  font-size: clamp(3.1rem, 4.5vw, 4rem);
  font-weight: 700;
  line-height: 0.95;
  letter-spacing: -0.06em;
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.08em;
`;
const CounterValue = styled.span `
  display: inline-block;
  min-width: 2ch;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;
const CounterSuffix = styled.span `
  display: inline-block;
  min-width: 1ch;
`;
const InactivityPopup21 = ({ continueHandler, resetViewHandler, maxTime = 15, }) => {
    const intervalRef = useRef(null);
    const [counter, setCounter] = useState(maxTime);
    const { t } = useTranslation();
    useEffect(() => {
        setCounter(maxTime);
    }, [maxTime]);
    useEffect(() => {
        intervalRef.current = setInterval(() => {
            setCounter((current) => {
                if (current <= 1) {
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    resetViewHandler();
                    return 0;
                }
                return current - 1;
            });
        }, 1000);
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [resetViewHandler]);
    return (_jsx(Overlay, Object.assign({ role: "button", tabIndex: 0, "aria-label": t('floor.inactive.popup.overlay-label'), onClick: continueHandler, onKeyDown: (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                continueHandler();
            }
        } }, { children: _jsxs(Bubble, Object.assign({ onClick: continueHandler }, { children: [_jsx(Illustration, { src: resetPopupGraphic, alt: "", "aria-hidden": "true" }), _jsxs(CopyBlock, { children: [_jsxs(Headline, { children: [_jsx(Hey, { children: t('floor.inactive.popup.hey') }), _jsx(Question, { children: t('floor.inactive.popup.question') })] }), _jsx(Body, { children: t('floor.inactive.popup.body') }), _jsx(CountdownLabel, { children: t('floor.inactive.popup.countdown-label') }), _jsxs(Counter, { children: [_jsx(CounterValue, { children: counter }), _jsx(CounterSuffix, { children: "s" })] })] })] })) })));
};
export default InactivityPopup21;
